import { formatDate } from '@angular/common';

/**
 * Checks if value is string.
 * @param value Value to check.
 * @returns True if value is string; otherwise false.
 */
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Removed diacritics from text.
 * @param text Text to remove diacritics from.
 * @returns Text without diacritics.
 */
export function removeDiacritics(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Removes white spaces from text.
 * @param text Text to remove white spaces from.
 * @returns Text without white spaces.
 */
export function removeWhiteSpace(text: string): string {
  return text.replace(/\s/g, '');
}

/**
 * Escapes HTML special characters, so text can be inserted into HTML markup.
 * @param text Text to escape.
 * @returns Text with &, <, >, " and ' replaced by HTML entities.
 */
export function escapeHtml(text: string): string {
  return text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

/**
 * Extracts user name from e-mail address.
 * @param email E-mail address.
 * @returns User name from e-mail address.
 */
export function extractNameFromEmail(email: string): string {
  if (email.includes('@')) {
    const username = email.substring(0, email.indexOf('@'));
    return username
      .split(/[-+_.]/)
      .map((name) => capitalizeFirstLetter(name))
      .join(' ');
  }
  return email;
}

/**
 * Capitalize first letter of text.
 * @param text Text to capitalize first letter.
 * @returns Text with capitalized first letter.
 */
export function capitalizeFirstLetter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Gets hash code of input string.
 * @param input Input string for hash code.
 * @returns Hash code from input string.
 */
export function getHashCode(input: string): number {
  let hash = 0;
  let i = 0;
  const len = input.length;
  while (i < len) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash + input.charCodeAt(i++)) << 0;
  }
  return hash;
}

/**
 * Returns initials from text.
 * @param text Text to get initials from.
 * @param maxLength Maximum length of initials.
 * @returns Initials from text.
 */
export function getInitials(text: string, maxLength = 2): string {
  let initials = '';

  if (text) {
    const names = text.split(/[ -]/).filter((name) => name.length);

    if (names.length > maxLength) {
      for (let i = 0; i < maxLength - 1; i++) {
        initials += names[i][0];
      }
      initials += names[names.length - 1][0];
    } else if (names.length === maxLength) {
      for (const name of names) {
        initials += name[0];
      }
    } else {
      initials = text.substring(0, Math.min(maxLength, text.length));
    }

    initials = initials.toUpperCase();
  }

  return initials;
}

/**
 * Converts first letter of string to lowercase.
 * @param str
 */
export function firstToLowerCase(str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

/**
 * Strips company type extension from company name.
 * @param companyName Company name with or without company type extension.
 * @returns Company name without company type extension.
 */
export function stripCompanyTypeExtension(companyName: string): string {
  return companyName
    ?.replace(/a\.\s?s\.$/, '')
    .replace(/s\.\s?r\.\s?o\.$/, '')
    .replace(/spol\.\s?s\s?r\.\s?o\.$/, '')
    .replace(/v\.\s?o\.\s?s\.$/, '')
    .replace(/k\.\s?s\.$/, '')
    .replace(/(P|p)lc\.?$/, '')
    .replace(/(L|l)td\.?$/, '')
    .trim();
}

/**
 * Converts string to camel case string.
 * @param word String to camel case.
 * @returns Came case string.
 */
export function camelCaseString(word: string): string {
  return word.charAt(0).toLowerCase() + word.slice(1);
}

/**
 * Compares strings ingoring case sensitivity.
 * @param string1 String 1 to compare.
 * @param string2 String 2 to compare.
 * @returns True if strings are equal; otherwise false.
 */
export function compareStringsIgnoreCase(string1: string, string2: string): boolean {
  return string1.toLowerCase() === string2.toLowerCase();
}

/**
 * Prepares variable symbol from document number.
 * @param documentNumber Document number.
 * @returns Variable symbol.
 */
export function prepareVariableSymbolFromDocumentNumber(documentNumber: string): string {
  if (!documentNumber) {
    return '';
  }
  if (documentNumber.length > 100) {
    return documentNumber.slice(0, 100);
  }
  return documentNumber;
}

/**
 * Prepares variable symbol from document number.
 * @param documentNumber Document number.
 * @returns Variable symbol.
 */
export function prepareVariableSymbolFromDocumentNumberOnlyDigits(documentNumber: string): string {
  if (!documentNumber) {
    return '';
  }
  const variableSymbol = documentNumber.replace(/\D+/g, '');
  if (variableSymbol.length > 10) {
    return variableSymbol.slice(-10);
  }
  return variableSymbol;
}

/**
 * Inserts character into string at specified index.
 * @param expr String to insert character into.
 * @param indexToInsert Index to insert character at.
 * @param character Character to insert.
 * @returns String with inserted character to specified index.
 */
export function insertCharacterInString(expr: string, indexToInsert: number, character: string): string {
  const leftPart = expr.substring(0, indexToInsert);
  const rightPart = expr.substring(indexToInsert);

  return leftPart + character + rightPart;
}

/**
 * Replaces range [start, end) in string with replacement — e.g. what a user selected before pasting.
 * With start === end nothing is removed, only inserted.
 * @param expr String to modify.
 * @param start Start index of the replaced range (inclusive).
 * @param end End index of the replaced range (exclusive).
 * @param replacement Text to put in place of the range.
 * @returns String with the range replaced.
 */
export function replaceRangeInString(expr: string, start: number, end: number, replacement: string): string {
  return expr.substring(0, start) + replacement + expr.substring(end);
}

/**
 * Inserts date stamp into file name.
 * @param friendlyName File name.
 * @param uploadTime Upload time.
 * @returns Date stamp inserted into file name.
 */
export function insertDateStampIntoFileName(friendlyName: string, uploadTime: Date): string {
  const index = friendlyName.lastIndexOf('.');
  const name = friendlyName.slice(0, index);
  const suffix = friendlyName.slice(index + 1);
  const timeStamp = formatDate(uploadTime, 'MMddHHmmSSS', 'sk');
  return `${name}_${timeStamp}.${suffix}`;
}

/**
 * Transforms string to uppercase.
 * @param value String to transform to uppercase.
 * @returns String in uppercase.
 */
export const transformToUppercase = (value: string): string => {
  return value?.toUpperCase() || value;
};

/**
 * Starts with ignore case and diacritics.
 * @param string String to search in.
 * @param searchString search string.
 * @returns True if string starts with search string; otherwise false.
 */
export const startsWithIgnoreCaseAndDiacritics = (string: string, searchString: string): boolean =>
  removeDiacritics(string.toLowerCase()).startsWith(removeDiacritics(searchString.toLowerCase()));

export const stringFormat = (str: string, ...val: any[]): string => {
  for (let index = 0; index < val.length; index++) {
    str = str.replace(`{${index}}`, `${val[index]}`);
  }

  return str;
};

/**
 * Joins values into a single string, skipping the empty ones.
 * @param values Values to join, each trimmed of surrounding white space. Empty, null and undefined entries are skipped.
 * @param separator Separator inserted between the joined values.
 * @returns Trimmed values joined by the separator.
 */
export function joinNonEmpty(values: (string | null | undefined)[], separator: string): string {
  return values
    .map((value) => value?.trim() ?? '')
    .filter((value) => value.length > 0)
    .join(separator);
}

/**
 * Gets name with the lowest free ordinal suffix, so that it does not collide with existing names.
 * @param originalName Name to append the ordinal suffix to.
 * @param existingNames Already used names, compared ignoring case and surrounding white space. Empty entries are skipped.
 * @returns Original name suffixed with the lowest ordinal not present in existing names.
 */
export const getNextNumberedName = (originalName: string, existingNames: (string | undefined)[]): string => {
  let nextName = '';
  let ordinal = 1;

  do {
    nextName = `${originalName} (${ordinal})`;
    ordinal++;
  } while (existingNames.some((name) => name?.toLowerCase().trim() === nextName.toLowerCase().trim()));

  return nextName;
};
